/**
 * Level Up — side-by-side Sheet A / Sheet B allocation.
 *
 * THE ONLY page where the dual-sheet split is fully exposed. Both sheets
 * always level together: one combined "Level Up" click advances both by 1.
 *
 * Flow:
 *   1. DM picks a character from the roster.
 *   2. Page shows current sheet A and sheet B side by side.
 *   3. DM clicks "Level Up" → pending level is currentLevel + 1.
 *   4. For each sheet, the DM sees: new features, HP roll, ASI/feat if
 *      applicable, subclass choice if at subclass level.
 *   5. DM fills in choices, clicks "Commit" → PUT profile.
 *   6. On success, the page reloads the character.
 *
 * Mechanical skeleton: feature names only (no descriptions). The DM
 * references the Player's Handbook or D&D Beyond for full text.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import { isAdmin } from '../lib/auth';
import { useIdentity } from '../lib/ImpersonationContext';
import {
  CharacterBundle,
  DEFAULT_SESSION_ID,
  getCharacters,
  updateCharacterProfile,
} from '../lib/sessionApi';
import type { Character, SheetLedger, AbilityScore } from '../lib/character';
import {
  abilityModifier,
  totalHp,
  mergedScore,
  sheetScore,
  combinedLevel,
  proficiencyBonus,
} from '../lib/character';
import {
  lookupClass,
  isAsiLevel,
  getSpellSlots,
  spellSlotDiff,
  type ClassDef,
  type SpellSlotInfo,
} from '../lib/classData';
import {
  FEAT_DATA,
  lookupFeat,
  checkFeatPrereqs,
  type FeatDef,
} from '../lib/featData';

const ABILITIES: AbilityScore[] = ['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA'];

// ────────────────────────── helpers ──────────────────────────

/** Roll a single hit die (simulated). Returns a value in [1, sides]. */
function rollHitDie(sides: number): number {
  return Math.floor(Math.random() * sides) + 1;
}

/** Average HP for a hit die (rounded up, standard 5e rule). */
function averageHitDie(sides: number): number {
  return Math.ceil(sides / 2) + 1; // e.g. d8 → 5, d10 → 6, d12 → 7
}

/** Ordinal suffix for spell level display. */
function ordinal(n: number): string {
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  return `${n}th`;
}

// ────────────────────────── types ──────────────────────────

interface PendingLevel {
  /** The level being gained (e.g. going from 3→4, this is 4). */
  newLevel: number;
  /** Features the class gains at this level. */
  features: string[];
  /** Is this an ASI level for this class? */
  isAsi: boolean;
  /** Is this the subclass choice level? */
  isSubclassLevel: boolean;
  subclassLabel: string;
  classDef: ClassDef | null;
  /** Spell slot info at the NEW level (null for non-casters). */
  newSlots: SpellSlotInfo | null;
}

interface SheetDraft {
  /** HP gained this level (die roll or average). */
  hpGain: number | null;
  /** Whether the DM chose to take the average. */
  tookAverage: boolean;
  /** If ASI level: which ability gets +1 (first of the pair). */
  asi1: AbilityScore | '';
  /** If ASI level: which ability gets +1 (second of the pair). */
  asi2: AbilityScore | '';
  /** If ASI level and DM chose a feat instead. */
  featName: string;
  /** Whether DM is choosing a feat instead of ASI. */
  chooseFeat: boolean;
  /** Subclass name, if this is the subclass level. */
  subclass: string;
  /** Override prereq failures for the chosen feat. */
  overridePrereqs: boolean;
}

const emptyDraft = (): SheetDraft => ({
  hpGain: null,
  tookAverage: false,
  asi1: '',
  asi2: '',
  featName: '',
  chooseFeat: false,
  subclass: '',
  overridePrereqs: false,
});

// ────────────────────────── component ──────────────────────────

export default function LevelUp() {
  const { user } = useAuth();
  const { impersonating } = useIdentity();
  const dm = !impersonating && isAdmin(user);

  // Character roster
  const [characters, setCharacters] = useState<CharacterBundle[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pending level-up state
  const [leveling, setLeveling] = useState(false);
  const [draftA, setDraftA] = useState<SheetDraft>(emptyDraft());
  const [draftB, setDraftB] = useState<SheetDraft>(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Fetch characters
  const loadCharacters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCharacters(DEFAULT_SESSION_ID);
      setCharacters(data.characters);
      if (data.characters.length > 0 && !selectedId) {
        setSelectedId(data.characters[0].id);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load characters');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { loadCharacters(); }, [loadCharacters]);

  // Selected character
  const selected = characters.find(c => c.id === selectedId);
  const profile = selected?.profile as Character | null;

  // Pending level info for each sheet
  const getPending = (sheet: SheetLedger): PendingLevel | null => {
    if (!leveling || !sheet) return null;
    const newLevel = sheet.level + 1;
    if (newLevel > 20) return null;
    const def = lookupClass(sheet.className);
    const levelData = def?.levels[newLevel];
    return {
      newLevel,
      features: levelData?.features ?? [],
      isAsi: isAsiLevel(sheet.className, newLevel),
      isSubclassLevel: def ? def.subclassLevel === newLevel : false,
      subclassLabel: def?.subclassLabel ?? 'Subclass',
      classDef: def ?? null,
      newSlots: getSpellSlots(sheet.className, newLevel),
    };
  };

  const pendingA = profile?.sheetA ? getPending(profile.sheetA) : null;
  const pendingB = profile?.sheetB ? getPending(profile.sheetB) : null;

  // ── Start / cancel level up ──
  const startLevelUp = () => {
    setLeveling(true);
    setDraftA(emptyDraft());
    setDraftB(emptyDraft());
    setSaveError(null);
  };

  const cancelLevelUp = () => {
    setLeveling(false);
    setDraftA(emptyDraft());
    setDraftB(emptyDraft());
    setSaveError(null);
  };

  // ── Roll HP ──
  const rollHp = (sheet: 'A' | 'B') => {
    const pending = sheet === 'A' ? pendingA : pendingB;
    const setDraft = sheet === 'A' ? setDraftA : setDraftB;
    if (!pending?.classDef) return;
    const roll = rollHitDie(pending.classDef.hitDie);
    setDraft(d => ({ ...d, hpGain: roll, tookAverage: false }));
  };

  const takeAverageHp = (sheet: 'A' | 'B') => {
    const pending = sheet === 'A' ? pendingA : pendingB;
    const setDraft = sheet === 'A' ? setDraftA : setDraftB;
    if (!pending?.classDef) return;
    const avg = averageHitDie(pending.classDef.hitDie);
    setDraft(d => ({ ...d, hpGain: avg, tookAverage: true }));
  };

  // ── Feat prereq checking ──
  const getFeatPrereqStatus = (
    draft: SheetDraft,
    sheetLabel: 'A' | 'B',
  ): { warn: boolean; failures: string[]; note?: string } => {
    if (!draft.chooseFeat || !draft.featName.trim() || !profile) {
      return { warn: false, failures: [] };
    }
    const feat = lookupFeat(draft.featName);
    if (!feat) return { warn: false, failures: [] }; // homebrew — can't validate

    // Build scores for the appropriate sheet
    const scores: Partial<Record<AbilityScore, number>> = {};
    for (const stat of ABILITIES) {
      scores[stat] = sheetLabel === 'A'
        ? sheetScore(profile as Character, 'A', stat)
        : mergedScore(profile as Character, stat);
    }

    const result = checkFeatPrereqs(feat, scores);
    return {
      warn: !result.met,
      failures: result.failures,
      note: feat.note,
    };
  };

  const prereqStatusA = getFeatPrereqStatus(draftA, 'A');
  const prereqStatusB = getFeatPrereqStatus(draftB, 'B');

  const hasUnoverriddenPrereqFailure =
    (prereqStatusA.warn && !draftA.overridePrereqs) ||
    (prereqStatusB.warn && !draftB.overridePrereqs);

  // ── ASI validation ──
  // Standard ASI: exactly +2 total (either +2 to one or +1/+1 to two)
  const asiValid = (draft: SheetDraft, pending: PendingLevel | null): boolean => {
    if (!pending?.isAsi || draft.chooseFeat) return true;
    const picks = [draft.asi1, draft.asi2].filter(Boolean);
    return picks.length >= 1 && picks.length <= 2;
  };

  // ── Validate & commit ──
  const canCommit = (): boolean => {
    if (!profile || !pendingA || !pendingB) return false;
    // Both sheets must have HP
    if (draftA.hpGain == null || draftB.hpGain == null) return false;
    // ASI validation: at least one pick when ASI is chosen
    if (!asiValid(draftA, pendingA)) return false;
    if (!asiValid(draftB, pendingB)) return false;
    // Feat must have a name if choosing feat
    if (pendingA.isAsi && draftA.chooseFeat && !draftA.featName.trim()) return false;
    if (pendingB.isAsi && draftB.chooseFeat && !draftB.featName.trim()) return false;
    // Subclass required at subclass level
    if (pendingA.isSubclassLevel && !profile.sheetA.subclass && !draftA.subclass) return false;
    if (pendingB.isSubclassLevel && !profile.sheetB.subclass && !draftB.subclass) return false;
    // Feat prereq failures block unless overridden
    if (hasUnoverriddenPrereqFailure) return false;
    return true;
  };

  const commitLevelUp = async () => {
    if (!profile || !pendingA || !pendingB) return;
    setSaving(true);
    setSaveError(null);

    try {
      // Build the updated profile
      const applyDraft = (sheet: SheetLedger, draft: SheetDraft, pending: PendingLevel): SheetLedger => {
        const updated = { ...sheet };
        updated.level = pending.newLevel;
        updated.hitDieTotal = sheet.hitDieTotal + (draft.hpGain ?? 0);

        // ASI deltas
        if (pending.isAsi && !draft.chooseFeat) {
          const newDeltas = { ...sheet.asiDeltas };
          if (draft.asi1) {
            newDeltas[draft.asi1] = (newDeltas[draft.asi1] ?? 0) + 1;
          }
          if (draft.asi2) {
            newDeltas[draft.asi2] = (newDeltas[draft.asi2] ?? 0) + 1;
          }
          updated.asiDeltas = newDeltas;
        }

        // Feat
        if (pending.isAsi && draft.chooseFeat && draft.featName.trim()) {
          updated.feats = [...sheet.feats, draft.featName.trim()];
        }

        // Subclass
        if (pending.isSubclassLevel && draft.subclass.trim()) {
          updated.subclass = draft.subclass.trim();
        }

        return updated;
      };

      const newProfile: Character = {
        ...profile,
        sheetA: applyDraft(profile.sheetA, draftA, pendingA),
        sheetB: applyDraft(profile.sheetB, draftB, pendingB),
      };

      await updateCharacterProfile(profile.id, newProfile as unknown as Record<string, unknown>);

      // Success — reload and exit leveling mode
      setLeveling(false);
      setDraftA(emptyDraft());
      setDraftB(emptyDraft());
      await loadCharacters();
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Merged preview (computed from hypothetical post-levelup state) ──
  const mergedPreview = useMemo(() => {
    if (!profile || !pendingA || !pendingB || !leveling) return null;

    // Simulate applying drafts
    const simSheet = (sheet: SheetLedger, draft: SheetDraft, pending: PendingLevel): SheetLedger => {
      const s = { ...sheet, asiDeltas: { ...sheet.asiDeltas }, feats: [...sheet.feats] };
      s.level = pending.newLevel;
      s.hitDieTotal = sheet.hitDieTotal + (draft.hpGain ?? 0);
      if (pending.isAsi && !draft.chooseFeat) {
        if (draft.asi1) s.asiDeltas[draft.asi1] = (s.asiDeltas[draft.asi1] ?? 0) + 1;
        if (draft.asi2) s.asiDeltas[draft.asi2] = (s.asiDeltas[draft.asi2] ?? 0) + 1;
      }
      if (pending.isAsi && draft.chooseFeat && draft.featName.trim()) {
        s.feats = [...s.feats, draft.featName.trim()];
      }
      if (pending.isSubclassLevel && draft.subclass.trim()) {
        s.subclass = draft.subclass.trim();
      }
      return s;
    };

    const futureChar: Character = {
      ...profile,
      sheetA: simSheet(profile.sheetA, draftA, pendingA),
      sheetB: simSheet(profile.sheetB, draftB, pendingB),
    };

    const currentChar = profile as Character;

    // Compute diffs
    const changes: string[] = [];

    // Level
    const oldCombined = combinedLevel(currentChar);
    const newCombined = combinedLevel(futureChar);
    changes.push(`Combined level ${oldCombined} \u2192 ${newCombined}`);

    // Prof bonus
    const oldPB = proficiencyBonus(currentChar);
    const newPB = proficiencyBonus(futureChar);
    if (oldPB !== newPB) {
      changes.push(`Proficiency bonus +${oldPB} \u2192 +${newPB}`);
    }

    // HP
    const oldHP = totalHp(currentChar);
    const newHP = totalHp(futureChar);
    changes.push(`Max HP ${oldHP} \u2192 ${newHP} (+${newHP - oldHP})`);

    // Stat changes
    for (const stat of ABILITIES) {
      const oldVal = mergedScore(currentChar, stat);
      const newVal = mergedScore(futureChar, stat);
      if (oldVal !== newVal) {
        const oldMod = abilityModifier(oldVal);
        const newMod = abilityModifier(newVal);
        const modStr = oldMod !== newMod
          ? ` (mod ${oldMod >= 0 ? '+' : ''}${oldMod} \u2192 ${newMod >= 0 ? '+' : ''}${newMod})`
          : '';
        changes.push(`${stat} ${oldVal} \u2192 ${newVal}${modStr}`);
      }
    }

    // New feats
    if (draftA.chooseFeat && draftA.featName.trim()) {
      changes.push(`Sheet A gains feat: ${draftA.featName.trim()}`);
    }
    if (draftB.chooseFeat && draftB.featName.trim()) {
      changes.push(`Sheet B gains feat: ${draftB.featName.trim()}`);
    }

    // Spell slot changes
    const slotDiffA = spellSlotDiff(profile.sheetA.className, profile.sheetA.level, pendingA.newLevel);
    const slotDiffB = spellSlotDiff(profile.sheetB.className, profile.sheetB.level, pendingB.newLevel);

    for (const [lv, diff] of Object.entries(slotDiffA.levelChanges)) {
      changes.push(`Sheet A ${ordinal(Number(lv))}-level slots: ${diff.from} \u2192 ${diff.to}`);
    }
    if (slotDiffA.pactChange) {
      changes.push(`Sheet A pact slots: ${slotDiffA.pactChange}`);
    }
    for (const [lv, diff] of Object.entries(slotDiffB.levelChanges)) {
      changes.push(`Sheet B ${ordinal(Number(lv))}-level slots: ${diff.from} \u2192 ${diff.to}`);
    }
    if (slotDiffB.pactChange) {
      changes.push(`Sheet B pact slots: ${slotDiffB.pactChange}`);
    }

    return { futureChar, changes };
  }, [profile, pendingA, pendingB, leveling, draftA, draftB]);

  // ── Render ──

  if (!dm) {
    return (
      <div className="levelup-page">
        <h1>Level Up</h1>
        <p className="muted">Only the DM can level up characters.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="levelup-page">
        <h1>Level Up</h1>
        <p className="muted">Loading characters...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="levelup-page">
        <h1>Level Up</h1>
        <p style={{ color: 'var(--danger)' }}>{error}</p>
        <button onClick={loadCharacters}>Retry</button>
      </div>
    );
  }

  if (characters.length === 0) {
    return (
      <div className="levelup-page">
        <h1>Level Up</h1>
        <p className="muted">No characters yet. Create one on the Sheet page first.</p>
      </div>
    );
  }

  return (
    <div className="levelup-page">
      <div className="levelup-header">
        <h1>Level Up</h1>
        <div className="levelup-header__controls">
          <select
            className="levelup-picker"
            value={selectedId ?? ''}
            onChange={e => {
              setSelectedId(e.target.value);
              cancelLevelUp();
            }}
          >
            {characters.map(c => (
              <option key={c.id} value={c.id}>
                {c.profile?.name ?? c.id} (Lv {((c.profile?.sheetA?.level ?? 0) + (c.profile?.sheetB?.level ?? 0))})
              </option>
            ))}
          </select>

          {!leveling && profile && (profile.sheetA.level < 20) && (
            <button className="btn-levelup" onClick={startLevelUp}>
              Level Up ({profile.sheetA.level} &rarr; {profile.sheetA.level + 1})
            </button>
          )}
          {leveling && (
            <button className="btn-cancel" onClick={cancelLevelUp}>Cancel</button>
          )}
        </div>
      </div>

      {profile && (
        <div className="levelup-summary">
          <span className="numeric">{profile.name}</span>
          <span className="muted"> &mdash; Combined Lv {profile.sheetA.level + profile.sheetB.level}</span>
          <span className="muted"> &mdash; HP {totalHp(profile as Character)}</span>
          <span className="muted"> &mdash; Prof +{proficiencyBonus(profile as Character)}</span>
        </div>
      )}

      {/* Side-by-side sheets */}
      <div className="levelup-sheets">
        {profile?.sheetA && (
          <SheetPanel
            label="A"
            sheet={profile.sheetA}

            pending={pendingA}
            draft={draftA}
            setDraft={setDraftA}
            onRollHp={() => rollHp('A')}
            onTakeAverage={() => takeAverageHp('A')}
            base={profile.base}
            prereqStatus={prereqStatusA}
          />
        )}
        {profile?.sheetB && (
          <SheetPanel
            label="B"
            sheet={profile.sheetB}

            pending={pendingB}
            draft={draftB}
            setDraft={setDraftB}
            onRollHp={() => rollHp('B')}
            onTakeAverage={() => takeAverageHp('B')}
            base={profile.base}
            prereqStatus={prereqStatusB}
          />
        )}
      </div>

      {/* Merged preview */}
      {leveling && mergedPreview && (
        <div className="levelup-preview">
          <h2>Merged Preview</h2>
          <div className="levelup-preview__changes">
            {mergedPreview.changes.map((change, i) => (
              <div key={i} className="levelup-preview__line">{change}</div>
            ))}
          </div>
        </div>
      )}

      {/* Commit bar */}
      {leveling && (
        <div className="levelup-commit">
          {saveError && <p style={{ color: 'var(--danger)' }}>{saveError}</p>}
          <button
            className="btn-commit"
            disabled={!canCommit() || saving}
            onClick={commitLevelUp}
          >
            {saving ? 'Saving...' : 'Commit Level Up'}
          </button>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── SheetPanel ──────────────────────────

interface SheetPanelProps {
  label: 'A' | 'B';
  sheet: SheetLedger;
  pending: PendingLevel | null;
  draft: SheetDraft;
  setDraft: React.Dispatch<React.SetStateAction<SheetDraft>>;
  onRollHp: () => void;
  onTakeAverage: () => void;
  base: Record<string, number>;
  prereqStatus: { warn: boolean; failures: string[]; note?: string };
}

function SheetPanel({
  label, sheet, pending, draft, setDraft,
  onRollHp, onTakeAverage, base, prereqStatus,
}: SheetPanelProps) {
  const cls = lookupClass(sheet.className);
  const colorClass = label === 'A' ? 'sheet-a' : 'sheet-b';
  const bgClass = label === 'A' ? 'sheet-a-bg' : 'sheet-b-bg';
  const [featSuggestions, setFeatSuggestions] = useState<FeatDef[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Current effective stat for this sheet's display
  const effectiveStat = (stat: AbilityScore): number => {
    return (base[stat] ?? 10) +
      (sheet.asiDeltas?.[stat] ?? 0) +
      (sheet.featDeltas?.[stat] ?? 0) +
      (sheet.magicItemDeltas?.[stat] ?? 0);
  };

  // Current spell slots for display
  const currentSlots = getSpellSlots(sheet.className, sheet.level);

  // Feat autocomplete handler
  const handleFeatInput = (value: string) => {
    setDraft(d => ({ ...d, featName: value, overridePrereqs: false }));
    if (value.trim().length >= 1) {
      const lower = value.toLowerCase();
      const matches = FEAT_DATA.filter(f => f.name.toLowerCase().includes(lower));
      setFeatSuggestions(matches.slice(0, 8));
      setShowSuggestions(true);
    } else {
      setFeatSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectFeat = (feat: FeatDef) => {
    setDraft(d => ({ ...d, featName: feat.name, overridePrereqs: false }));
    setShowSuggestions(false);
  };

  return (
    <section className={`levelup-panel ${bgClass}`}>
      <h2 className={colorClass}>
        Sheet {label}: {sheet.className}
        {sheet.subclass ? ` \u2014 ${sheet.subclass}` : ''}
      </h2>

      {/* Current state */}
      <div className="levelup-panel__current">
        <div className="levelup-stat-row">
          <span className="muted">Level</span>
          <span className="numeric">{sheet.level}</span>
        </div>
        <div className="levelup-stat-row">
          <span className="muted">Hit Die</span>
          <span className="numeric">d{cls?.hitDie ?? '?'}</span>
        </div>
        <div className="levelup-stat-row">
          <span className="muted">Hit Die Total</span>
          <span className="numeric">{sheet.hitDieTotal}</span>
        </div>
        <div className="levelup-stat-row">
          <span className="muted">Feats</span>
          <span>{sheet.feats.length > 0 ? sheet.feats.join(', ') : 'None'}</span>
        </div>
      </div>

      {/* Stats overview */}
      <div className="levelup-stats-grid">
        {ABILITIES.map(stat => (
          <div key={stat} className="levelup-stat-cell">
            <span className="levelup-stat-label">{stat}</span>
            <span className="numeric">{effectiveStat(stat)}</span>
            <span className="muted numeric">
              ({abilityModifier(effectiveStat(stat)) >= 0 ? '+' : ''}{abilityModifier(effectiveStat(stat))})
            </span>
          </div>
        ))}
      </div>

      {/* Current spell slots (for casters) */}
      {currentSlots && (
        <div className="levelup-slots-current">
          <span className="muted">Spell Slots:</span>
          <span className="numeric">
            {currentSlots.pact
              ? `${currentSlots.pact.slots}x Lv${currentSlots.pact.slotLevel} pact`
              : Object.entries(currentSlots.leveled)
                  .map(([lv, count]) => `${ordinal(Number(lv))}: ${count}`)
                  .join(', ')
            }
          </span>
        </div>
      )}

      {/* Feature history */}
      <div className="levelup-features">
        <h3 className={colorClass}>Features (1&ndash;{sheet.level})</h3>
        {Array.from({ length: sheet.level }, (_, i) => i + 1).map(lv => {
          const lvData = cls?.levels[lv];
          const features = lvData?.features ?? [];
          if (features.length === 0) return null;
          return (
            <div key={lv} className="levelup-feature-row">
              <span className="numeric levelup-feature-lv">Lv {lv}</span>
              <span>{features.join(', ')}</span>
            </div>
          );
        })}
      </div>

      {/* Pending level-up section */}
      {pending && (
        <div className="levelup-pending">
          <h3 className={colorClass}>
            &rarr; Level {pending.newLevel}
          </h3>

          {/* New features */}
          {pending.features.length > 0 && (
            <div className="levelup-new-features">
              <span className="muted">New features:</span>
              <span className="levelup-feature-names">
                {pending.features.join(', ')}
              </span>
            </div>
          )}

          {/* Spell slot changes */}
          {pending.newSlots && (() => {
            const diff = spellSlotDiff(sheet.className, sheet.level, pending.newLevel);
            const hasChanges = Object.keys(diff.levelChanges).length > 0 || diff.pactChange;
            if (!hasChanges) return null;
            return (
              <div className="levelup-slot-changes">
                <span className="muted">Spell slot changes:</span>
                {Object.entries(diff.levelChanges).map(([lv, d]) => (
                  <span key={lv} className="levelup-slot-diff">
                    {ordinal(Number(lv))}-level: {d.from} &rarr; {d.to}
                  </span>
                ))}
                {diff.pactChange && (
                  <span className="levelup-slot-diff">Pact: {diff.pactChange}</span>
                )}
              </div>
            );
          })()}

          {/* HP Roll */}
          <div className="levelup-hp-section">
            <span className="muted">HP gain (d{cls?.hitDie ?? 8}):</span>
            <div className="levelup-hp-controls">
              <button onClick={onRollHp} className="btn-sm">
                Roll
              </button>
              <button onClick={onTakeAverage} className="btn-sm">
                Average ({averageHitDie(cls?.hitDie ?? 8)})
              </button>
              {draft.hpGain != null && (
                <span className="numeric levelup-hp-result">
                  +{draft.hpGain}
                  {draft.tookAverage ? ' (avg)' : ' (rolled)'}
                </span>
              )}
            </div>
          </div>

          {/* Subclass choice */}
          {pending.isSubclassLevel && !sheet.subclass && (
            <div className="levelup-subclass">
              <label className="muted">{pending.subclassLabel}:</label>
              <input
                type="text"
                placeholder={`Enter ${pending.subclassLabel}`}
                value={draft.subclass}
                onChange={e => setDraft(d => ({ ...d, subclass: e.target.value }))}
              />
            </div>
          )}

          {/* ASI / Feat */}
          {pending.isAsi && (
            <div className="levelup-asi">
              <div className="levelup-asi-toggle">
                <label>
                  <input
                    type="radio"
                    name={`asi-mode-${label}`}
                    checked={!draft.chooseFeat}
                    onChange={() => setDraft(d => ({
                      ...d,
                      chooseFeat: false,
                      overridePrereqs: false,
                    }))}
                  />
                  <span>Ability Score Improvement</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name={`asi-mode-${label}`}
                    checked={draft.chooseFeat}
                    onChange={() => setDraft(d => ({
                      ...d,
                      chooseFeat: true,
                      asi1: '',
                      asi2: '',
                    }))}
                  />
                  <span>Feat</span>
                </label>
              </div>

              {!draft.chooseFeat && (
                <div className="levelup-asi-picks">
                  <div className="levelup-asi-pick">
                    <label className="muted">+1 to:</label>
                    <select
                      value={draft.asi1}
                      onChange={e => setDraft(d => ({ ...d, asi1: e.target.value as AbilityScore | '' }))}
                    >
                      <option value="">— Choose —</option>
                      {ABILITIES.map(s => (
                        <option key={s} value={s}>
                          {s} ({effectiveStat(s)}{draft.asi1 === s || draft.asi2 === s ? ` \u2192 ${effectiveStat(s) + 1 + (draft.asi1 === s && draft.asi2 === s ? 1 : 0)}` : ''})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="levelup-asi-pick">
                    <label className="muted">+1 to:</label>
                    <select
                      value={draft.asi2}
                      onChange={e => setDraft(d => ({ ...d, asi2: e.target.value as AbilityScore | '' }))}
                    >
                      <option value="">— Choose —</option>
                      {ABILITIES.map(s => (
                        <option key={s} value={s}>
                          {s} ({effectiveStat(s)}{draft.asi1 === s || draft.asi2 === s ? ` \u2192 ${effectiveStat(s) + 1 + (draft.asi1 === s && draft.asi2 === s ? 1 : 0)}` : ''})
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="muted" style={{ fontSize: '0.8rem' }}>
                    Pick two abilities for +1 each, or the same ability twice for +2.
                  </p>
                </div>
              )}

              {draft.chooseFeat && (
                <div className="levelup-feat-input">
                  <label className="muted">Feat name:</label>
                  <div className="levelup-feat-autocomplete">
                    <input
                      type="text"
                      placeholder="Start typing to search feats..."
                      value={draft.featName}
                      onChange={e => handleFeatInput(e.target.value)}
                      onFocus={() => {
                        if (draft.featName.trim()) handleFeatInput(draft.featName);
                      }}
                      onBlur={() => {
                        // Delay to allow click on suggestion
                        setTimeout(() => setShowSuggestions(false), 200);
                      }}
                    />
                    {showSuggestions && featSuggestions.length > 0 && (
                      <ul className="levelup-feat-suggestions">
                        {featSuggestions.map(f => (
                          <li
                            key={f.name}
                            className="levelup-feat-suggestion"
                            onMouseDown={() => selectFeat(f)}
                          >
                            <span>{f.name}</span>
                            {Object.keys(f.prereqs).length > 0 && (
                              <span className="muted" style={{ fontSize: '0.75rem' }}>
                                {Object.entries(f.prereqs).map(([s, v]) => `${s} ${v}+`).join(', ')}
                              </span>
                            )}
                            {f.note && (
                              <span className="muted" style={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                                {f.note}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Prereq warning banner */}
                  {prereqStatus.warn && (
                    <div className="levelup-prereq-warning">
                      <span className="levelup-prereq-warning__icon">&#9888;</span>
                      <div className="levelup-prereq-warning__body">
                        <span>
                          Prerequisite not met: {prereqStatus.failures.join('; ')}
                        </span>
                        {prereqStatus.note && (
                          <span className="muted" style={{ fontSize: '0.8rem' }}>
                            Note: {prereqStatus.note}
                          </span>
                        )}
                        <label className="levelup-prereq-override">
                          <input
                            type="checkbox"
                            checked={draft.overridePrereqs}
                            onChange={e => setDraft(d => ({ ...d, overridePrereqs: e.target.checked }))}
                          />
                          <span>Override — DM approves this feat despite prereqs</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Note for feats without stat prereqs but with other requirements */}
                  {!prereqStatus.warn && prereqStatus.note && draft.featName.trim() && (
                    <p className="muted" style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                      Note: {prereqStatus.note}
                    </p>
                  )}

                  {/* Homebrew note if feat not in database */}
                  {draft.featName.trim() && !lookupFeat(draft.featName) && (
                    <p className="muted" style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                      Custom/homebrew feat — no prerequisite data available.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
